import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Mail } from 'lucide-react';

interface SendProfileNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileId: string;
  recipientEmail: string;
  recipientName: string;
}

export function SendProfileNotificationDialog({
  open,
  onOpenChange,
  profileId,
  recipientEmail,
  recipientName,
}: SendProfileNotificationDialogProps) {
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const loadAdminInfo = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get profile name
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      // Get email from profile_private
      const { data: privateProfile } = await supabase
        .from('profile_private')
        .select('email')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setAdminName(profile.full_name || 'Admin');
      }
      setAdminEmail(privateProfile?.email || user.email || '');
    };

    if (open) {
      loadAdminInfo();
    }
  }, [open]);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in both subject and message',
        variant: 'destructive',
      });
      return;
    }

    if (!adminEmail) {
      toast({
        title: 'Error',
        description: 'Could not determine your email address',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { error } = await supabase.functions.invoke('send-profile-notification', {
        body: {
          profileId: profileId,
          recipientEmail: recipientEmail,
          recipientName: recipientName,
          adminName: adminName,
          subject: subject.trim(),
          message: message.trim(),
          replyToEmail: adminEmail,
        },
      });

      if (error) throw error;

      toast({
        title: 'Email sent',
        description: `Notification email sent to ${recipientName}`,
      });

      setSubject('');
      setMessage('');
      onOpenChange(false);
    } catch (error: unknown) {
      console.error('Error sending notification:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Email {recipientName}</DialogTitle>
          <DialogDescription>
            Send an email directly to this user.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Email will be sent to:</span>
              <span className="font-medium">{recipientEmail}</span>
            </div>
            
            <div className="text-sm text-muted-foreground">
              From: <span className="font-medium text-foreground">{adminName}</span>
            </div>
            
            <div className="text-sm text-muted-foreground">
              Reply-to: <span className="font-medium text-foreground">{adminEmail}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              disabled={isSending}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message here..."
              rows={10}
              disabled={isSending}
            />
          </div>

          <div className="bg-accent/50 border border-border rounded-lg p-3 text-sm">
            <p className="font-semibold mb-1">The user will receive:</p>
            <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
              <li>An email at {recipientEmail}</li>
              <li>Your name ({adminName}) as the sender</li>
              <li>Your message content</li>
              <li>Ability to reply directly to you at {adminEmail}</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending}
          >
            {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Mail className="mr-2 h-4 w-4" />
            Send Email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
